import React, { useEffect, useState, useCallback } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { UserCog, Check, X as XIcon, ArrowLeft } from "lucide-react";

const STATUS_LABEL = { pending: "قيد المراجعة", approved: "موافَق عليه", rejected: "مرفوض", superseded: "مستبدَل" };
const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  superseded: "bg-gray-100 text-gray-600",
};
const FIELD_LABEL = { first_name: "الاسم", last_name: "الكنية", store_name: "اسم المحل" };

const RequestCard = ({ req, onDone }) => {
  const [edit, setEdit] = useState({ ...req.requested });
  const [busy, setBusy] = useState(false);
  const isPending = req.status === "pending";

  const act = async (action) => {
    setBusy(true);
    try {
      if (action === "approve") {
        await api.post(`/admin/name-requests/${req.id}/approve`, edit);
        toast.success("تمت الموافقة وتطبيق التعديل");
      } else {
        await api.post(`/admin/name-requests/${req.id}/reject`, {});
        toast.success("تم رفض الطلب");
      }
      onDone();
    } catch (e) { toast.error(arabicError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#7AB2B2] rounded-xl p-4 space-y-3" data-testid={`name-request-${req.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-extrabold text-[#333333]">{req.merchant?.store_name || req.current?.store_name || "—"}</div>
          <div className="text-xs text-[#666666]">{req.merchant_email} · {req.merchant?.city || ""}</div>
          <div className="text-xs text-[#666666]">{new Date(req.created_at).toLocaleString("ar-SY")}</div>
        </div>
        <span className={`text-xs font-extrabold px-2.5 py-1 rounded-full ${STATUS_COLOR[req.status]}`}>{STATUS_LABEL[req.status]}</span>
      </div>

      <div className="space-y-2">
        {Object.keys(req.requested || {}).map((f) => (
          <div key={f} className="flex items-center gap-2 text-sm flex-wrap">
            <span className="font-bold text-[#666666] w-20">{FIELD_LABEL[f]}:</span>
            <span className="text-[#666666] line-through">{req.current?.[f] || "—"}</span>
            <ArrowLeft size={14} className="text-[#09637E]" />
            {isPending ? (
              <input value={edit[f] ?? ""} onChange={(e) => setEdit((s) => ({ ...s, [f]: e.target.value }))}
                data-testid={`edit-${f}-${req.id}`}
                className="h-9 rounded-lg border border-[#7AB2B2] px-3 font-bold text-[#09637E] focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
            ) : (
              <span className="font-extrabold text-[#09637E]">{(req.applied || req.requested)?.[f]}</span>
            )}
          </div>
        ))}
      </div>

      {isPending && (
        <div className="flex gap-2">
          <button onClick={() => act("approve")} disabled={busy} data-testid={`approve-${req.id}`}
            className="h-10 px-5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm flex items-center gap-1 disabled:opacity-60">
            <Check size={16} /> موافقة وتطبيق
          </button>
          <button onClick={() => act("reject")} disabled={busy} data-testid={`reject-${req.id}`}
            className="h-10 px-5 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold text-sm flex items-center gap-1 disabled:opacity-60">
            <XIcon size={16} /> رفض
          </button>
        </div>
      )}
    </div>
  );
};

const AdminNameRequests = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/name-requests", { params: tab === "pending" ? { status: "pending" } : {} });
      setRows(r.data || []);
    } catch (e) { toast.error(arabicError(e)); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-name-requests-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333] flex items-center gap-2">
          <UserCog className="text-[#09637E]" size={32} /> طلبات تعديل الأسماء
        </h1>
        <p className="text-[#666666] mt-1">مراجعة طلبات تعديل الاسم الشخصي واسم المحل — يمكنك التعديل قبل الموافقة</p>
      </div>

      <div className="flex gap-2">
        {[["pending", "قيد المراجعة"], ["all", "الكل"]].map(([v, label]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
            className={`h-11 px-6 rounded-lg font-bold ${tab === v ? "bg-[#09637E] text-white" : "bg-white text-[#09637E] border border-[#7AB2B2]"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-[#666666] py-12">جارٍ التحميل...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#7AB2B2] rounded-2xl p-12 text-center" data-testid="empty-name-requests">
          <UserCog className="mx-auto text-[#7AB2B2] mb-3" size={48} />
          <p className="text-[#666666] font-bold text-lg">لا توجد طلبات {tab === "pending" ? "قيد المراجعة" : ""}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => <RequestCard key={r.id} req={r} onDone={load} />)}
        </div>
      )}
    </div>
  );
};

export default AdminNameRequests;
